import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MainLayout from './shared/layouts/MainLayout';
import AuthLayout from './shared/layouts/AuthLayout';
import Login from './modules/base/pages/Login';
import Dashboard from './modules/base/pages/Dashboard';
import ProductsList from './modules/products/pages/ProductsList';
import ProductForm from './modules/products/pages/ProductForm';
import ProductsByCategory from './modules/products/pages/ProductsByCategory';
import CategoriesManagement from './modules/products/pages/CategoriesManagement';
import InventoryDashboard from './modules/inventory/pages/InventoryDashboard';
import SalesOrdersList from './modules/sales/pages/SalesOrdersList';
import SalesOrderForm from './modules/sales/pages/SalesOrderForm';
import SalesOrderPrint from './modules/sales/pages/SalesOrderPrint';
import SalesOrdersPrint from './modules/sales/pages/SalesOrdersPrint';
import CustomersList from './modules/sales/pages/CustomersList';
import CustomerForm from './modules/sales/pages/CustomerForm';
import CustomerDetails from './modules/sales/pages/CustomerDetails';
import PurchaseOrdersList from './modules/purchase/pages/PurchaseOrdersList';
import PurchaseOrderForm from './modules/purchase/pages/PurchaseOrderForm';
import PurchaseReturnsList from './modules/purchase/pages/PurchaseReturnsList';
import PurchaseReturnForm from './modules/purchase/pages/PurchaseReturnForm';
import SuppliersList from './modules/purchase/pages/SuppliersList';
import SupplierDetails from './modules/purchase/pages/SupplierDetails';
import Reports from './modules/reporting/pages/Reports';
import ReportCategoryView from './modules/reporting/pages/ReportCategoryView';
import ReportView from './modules/reporting/pages/ReportView';
import Expenses from './modules/finance/pages/Expenses';
import VanTripsList from './modules/van-sales/pages/VanTripsList';
import VansList from './modules/van-sales/pages/VansList';
import VanTripDetail from './modules/van-sales/pages/VanTripDetail';
import VanReconciliation from './modules/van-sales/pages/VanReconciliation';
import Settings from './modules/settings/pages/Settings';
import Profile from './modules/base/pages/Profile';
import UsersList from './modules/base/pages/UsersList';
import RolesList from './modules/base/pages/RolesList';
import { selectIsAuthenticated } from './store/slices/authSlice';
import { ProtectedRoute } from './shared/components/PermissionGate';

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <AuthLayout>
              <Login />
            </AuthLayout>
          )
        }
      />
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <MainLayout />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        <Route path="dashboard" element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
        <Route path="products" element={<ProtectedRoute permission="products.products.read"><ProductsList /></ProtectedRoute>} />
        <Route path="products/category-tree" element={<ProtectedRoute permission="products.products.read"><ProductsByCategory /></ProtectedRoute>} />
        <Route path="products/categories" element={<ProtectedRoute permission="products.categories.read"><CategoriesManagement /></ProtectedRoute>} />
        <Route path="products/new" element={<ProtectedRoute permission="products.products.create"><ProductForm /></ProtectedRoute>} />
        <Route path="products/:id" element={<ProtectedRoute permission="products.products.read"><ProductForm /></ProtectedRoute>} />
        <Route path="inventory" element={<ProtectedRoute module="inventory"><InventoryDashboard /></ProtectedRoute>} />
        <Route path="sales" element={<ProtectedRoute permission="sales.orders.read"><SalesOrdersList /></ProtectedRoute>} />
        <Route path="sales/orders/new" element={<ProtectedRoute permission="sales.orders.create"><SalesOrderForm /></ProtectedRoute>} />
        <Route path="sales/orders/:id" element={<ProtectedRoute permission="sales.orders.read"><SalesOrderForm /></ProtectedRoute>} />
        <Route path="sales/orders/:id/print" element={<ProtectedRoute permission="sales.orders.read"><SalesOrderPrint /></ProtectedRoute>} />
        <Route path="sales/print" element={<ProtectedRoute permission="sales.orders.read"><SalesOrdersPrint /></ProtectedRoute>} />
        <Route path="purchases" element={<ProtectedRoute permission="purchases.orders.read"><PurchaseOrdersList /></ProtectedRoute>} />
        <Route path="purchases/returns" element={<ProtectedRoute permission="purchases.returns.read"><PurchaseReturnsList /></ProtectedRoute>} />
        <Route path="purchases/returns/new" element={<ProtectedRoute permission="purchases.returns.create"><PurchaseReturnForm /></ProtectedRoute>} />
        <Route path="purchases/returns/:id" element={<ProtectedRoute permission="purchases.returns.read"><PurchaseReturnForm /></ProtectedRoute>} />
        <Route path="purchases/suppliers" element={<ProtectedRoute permission="purchases.suppliers.read"><SuppliersList /></ProtectedRoute>} />
        <Route path="purchases/suppliers/:id" element={<ProtectedRoute permission="purchases.suppliers.read"><SupplierDetails /></ProtectedRoute>} />
        <Route path="purchases/orders/new" element={<ProtectedRoute permission="purchases.orders.create"><PurchaseOrderForm /></ProtectedRoute>} />
        <Route path="purchases/orders/:id" element={<ProtectedRoute permission="purchases.orders.read"><PurchaseOrderForm /></ProtectedRoute>} />
        <Route path="customers" element={<ProtectedRoute permission="sales.customers.read"><CustomersList /></ProtectedRoute>} />
        <Route path="customers/new" element={<ProtectedRoute permission="sales.customers.create"><CustomerForm /></ProtectedRoute>} />
        <Route path="customers/:id" element={<ProtectedRoute permission="sales.customers.read"><CustomerForm /></ProtectedRoute>} />
        <Route path="customers/:id/details" element={<ProtectedRoute permission="sales.customers.read"><CustomerDetails /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute permission="users.users.read"><UsersList /></ProtectedRoute>} />
        <Route path="roles" element={<ProtectedRoute permission="roles.roles.read"><RolesList /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute module="reports"><Reports /></ProtectedRoute>} />
        <Route path="reports/category/:categoryId" element={<ProtectedRoute module="reports"><ReportCategoryView /></ProtectedRoute>} />
        <Route path="reports/view/:reportId" element={<ProtectedRoute module="reports"><ReportView /></ProtectedRoute>} />
        <Route path="van-sales" element={<ProtectedRoute module="van-sales"><VanTripsList /></ProtectedRoute>} />
        <Route path="van-sales/vans" element={<ProtectedRoute permission="van-sales.vans.read"><VansList /></ProtectedRoute>} />
        <Route path="van-sales/trips/new" element={<ProtectedRoute permission="van-sales.trips.create"><VanTripDetail /></ProtectedRoute>} />
        <Route path="van-sales/trips/:id" element={<ProtectedRoute permission="van-sales.trips.read"><VanTripDetail /></ProtectedRoute>} />
        <Route path="van-sales/trips/:id/reconciliation" element={<ProtectedRoute permission="van-sales.trips.read"><VanReconciliation /></ProtectedRoute>} />
        <Route path="expenses" element={<ProtectedRoute module="expenses"><Expenses /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute module="settings"><Settings /></ProtectedRoute>} />
        <Route path="profile" element={<Profile />} />
        <Route path="" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default App;
